import { initWasm } from "../../lib/wasm";

let initialization: Promise<void> | null = null;
export function initializeApplication(): Promise<void> {
  initialization ??= (async () => {
    if ("serviceWorker" in navigator) {
      const workers = navigator.serviceWorker;
      await withTimeout(workers.register("/sw.js"), "Service Worker registration");
      const registration = await withTimeout(workers.ready, "Service Worker activation");
      // ready means an active registration, not that this first page is controlled.
      if (!workers.controller) {
        await new Promise<void>((resolve, reject) => {
          const clean = () => {
            clearTimeout(timer);
            workers.removeEventListener("controllerchange", change);
          };
          const change = () => {
            if (workers.controller) {
              clean();
              resolve();
            }
          };
          const timer = setTimeout(() => {
            clean();
            reject(new Error("Service Worker did not take control"));
          }, 15_000);
          workers.addEventListener("controllerchange", change);
          // A hard reload may bypass an already active worker. It will not
          // activate again, so explicitly ask it to claim this new page.
          registration.active?.postMessage({ t: "echofloat:claim" });
          change();
        });
      }
    }
    await withTimeout(initWasm(), "WebAssembly initialization");
  })().catch((error: unknown) => {
    initialization = null;
    throw error;
  });
  return initialization;
}
function withTimeout<T>(operation: Promise<T>, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out`)), 15_000);
    operation.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** Module ownership survives StrictMode, but never an HMR disposal. */
export function createStartupLoader<T>(initialize: () => Promise<T>, start: () => void) {
  let cancelled = false;
  let loading: Promise<T> | null = null;
  return {
    load(): Promise<T> {
      if (cancelled) return Promise.reject(new DOMException("startup disposed", "AbortError"));
      loading ??= initialize()
        .then((application) => {
          if (cancelled) throw new DOMException("startup disposed", "AbortError");
          start();
          return application;
        })
        .catch((error: unknown) => {
          loading = null;
          throw error;
        });
      return loading;
    },
    cancel(): void {
      cancelled = true;
    },
  };
}
